// Admin → Ambassadors: the Strategic Influence Score. Instead of guessing the
// strategic multiplier, the admin rates the DJ 0–10 on four factors and the
// weighted average becomes the multiplier:
//
//   score      = Σ rating × weight ÷ Σ weight        (the weights add up to 100%)
//   multiplier = 1 + (score − 5) × 0.20, kept between 0.5× and 2.0×
//
// 5 is neutral (1.00×). The factor names and weights are shared defaults the
// admin changes in Settings; each offer saves the ones it was scored with,
// matched by a stable id, so renaming or re-weighting never changes a saved
// offer or loses a rating. No React, no network.
import type { FactorDef, ScoreFactor } from "../../types/ambassadors";

export const NEUTRAL_RATING = 5;
const STEP = 0.2;
const MIN_MULTIPLIER = 0.5;
const MAX_MULTIPLIER = 2;

export const DEFAULT_FACTORS: FactorDef[] = [
  { id: "industry", name: "Industry Influence", weight: 30 },
  { id: "acquisition", name: "DJ Acquisition Potential", weight: 35 },
  { id: "commercial", name: "Commercial Influence", weight: 20 },
  { id: "brand", name: "Brand Credibility", weight: 15 },
];

// The question each factor answers, shown as the hint in its note box.
export const FACTOR_HINTS: Record<string, string> = {
  industry: "How respected and connected is this DJ?",
  acquisition: "Can they convince other DJs to join Request?",
  commercial: "Can they bring venues, promoters, festivals or other partners?",
  brand: "Does being linked with this DJ strengthen Request’s brand?",
};

export const neutralFactors = (defs: FactorDef[]): ScoreFactor[] =>
  defs.map((d) => ({ ...d, rating: NEUTRAL_RATING, note: "" }));

// The weighted average, 0–10; null if a rating or weight is unusable.
export function strategicScore(factors: ScoreFactor[]): number | null {
  let total = 0;
  let weighted = 0;
  for (const f of factors) {
    if (!(f.rating >= 0 && f.rating <= 10) || !(f.weight >= 0)) return null;
    total += f.weight;
    weighted += f.rating * f.weight;
  }
  return total > 0 ? weighted / total : null;
}

export const multiplierFor = (score: number): number =>
  Math.min(MAX_MULTIPLIER, Math.max(MIN_MULTIPLIER, 1 + (score - NEUTRAL_RATING) * STEP));

// New shared names and weights for an offer: matched by id, so every rating
// and note stays with its factor; a factor new to the offer starts neutral.
export function withFactorDefs(factors: ScoreFactor[], defs: FactorDef[]): ScoreFactor[] {
  return defs.map((d) => {
    const had = factors.find((f) => f.id === d.id);
    return { ...d, rating: had ? had.rating : NEUTRAL_RATING, note: had ? had.note : "" };
  });
}

export const weightTotal = (defs: FactorDef[]): number => defs.reduce((s, d) => s + d.weight, 0);

// What Settings may save: every factor named, the weights adding up to exactly 100%.
export const validFactorDefs = (defs: FactorDef[]): boolean =>
  defs.length > 0 &&
  defs.every((d) => d.name.trim() && Number.isFinite(d.weight) && d.weight >= 0) &&
  Math.abs(weightTotal(defs) - 100) < 0.001;

type Loose = Record<string, unknown>;
const obj = (v: unknown): Loose => (v && typeof v === "object" && !Array.isArray(v) ? (v as Loose) : {});
const num = (v: unknown): number => (typeof v === "number" && Number.isFinite(v) ? v : NaN);

// The shared names and weights as the server sent them; the defaults if there
// are none yet (or they don't hold together).
export function factorDefsFrom(raw: unknown): FactorDef[] {
  const list = (Array.isArray(raw) ? raw : []).map((f): FactorDef => {
    const o = obj(f);
    return { id: String(o.id ?? ""), name: String(o.name ?? ""), weight: num(o.weight) };
  });
  return list.length && list.every((d) => d.id) && validFactorDefs(list) ? list : DEFAULT_FACTORS;
}

// An offer's saved factors; null when it was saved before the score existed.
export function scoreFactorsFrom(raw: unknown): ScoreFactor[] | null {
  if (!Array.isArray(raw) || raw.length === 0) return null;
  return raw.map((f) => {
    const o = obj(f);
    const rating = num(o.rating);
    return {
      id: String(o.id ?? ""),
      name: String(o.name ?? ""),
      weight: num(o.weight),
      rating: rating >= 0 && rating <= 10 ? rating : NEUTRAL_RATING,
      note: typeof o.note === "string" ? o.note : "",
    };
  });
}
